import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
    Row,
    Col,
    CardHeader,
    CardBody,
    Spinner,
    Alert,
    Input,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Tooltip,
} from "reactstrap";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useSearchParams } from "react-router-dom";

import TableCard from "./Paymenttablecard";
import SharePaymentLinkPopover from "./SharePaymentLinkPopover";
import PaymentLinkDetailPanel from "../payment/PaymentLinkDetailPanel";
import PaymentLinkQrModal from "../modals/payment/PaymentLinkQrModal";
import PaymentLinkRotateTokenModal from "../modals/payment/PaymentLinkRotateTokenModal";
import PaymentLinkDeleteModal from "../modals/payment/PaymentLinkDeleteModal";
import InstantPaymentModal from "../modals/payment/instantPaymentCreatePage/InstantPaymentModal";

import { API } from "../../api/urls";
import MiniSparkline from "../../components/Common/MiniSparkline";
import { COLORS } from "./Paymenthistoryconstants";
import { getPaymentLinkStyles, PAYMENT_LINK_SCOPE } from "./Paymentlinkstyles";
import { getPaginationNumbers, copyToClipboard } from "./Paymenthistoryutils";
import {
    formatDate,
    formatTime,
    getLinkStatus,
    getClickedChip,
    getPaymentChip,
    canToggleLinkStatus,
    canDeleteLink,
} from "./Paymentlinkutils";
import { formatAmountWithCurrency } from "../modals/payment/utils/currency";
import { fetchAuthMe } from "../../api/requests/auth-requests";
import { fetchCompaniesList } from "../../api/requests/company-requests";
import {
    updatePaymentLinkStatus,
    rotatePaymentLinkToken,
    deletePaymentLink,
} from "../../api/requests/payment-requests";
import useAnimatedPlaceholder, { SEARCH_FIELDS } from "../../hooks/useAnimatedPlaceholder";
import TruncatedText from "../../components/Common/TruncatedText";
import DateInput from "../../components/Common/DateInput";
import { getPaymentWords } from "../../utils/paymentWord";

// Stil metni sabit; her render'da yeniden üretilirse React <style> düğümünü
// tazeler ve tarayıcı tüm kuralları yeniden ayrıştırır (panel açarken takılma sebebi).
const PAYMENT_LINK_STYLES = getPaymentLinkStyles();

const PAGE_SIZES = [10, 25, 50, 100];
const PANEL_SHARE_BTN_ID = "share-btn-detail-panel";

// Hızlı filtreler backend'in clicked/paid/expired parametrelerine birebir karşılık gelir.
const QUICK_FILTERS = [
    { key: "all", label: "Tümü", clicked: "", paid: "", expired: "", countKey: "total" },
    { key: "clicked", label: "Tıklandı", clicked: "true", paid: "", expired: "", countKey: "clicked" },
    { key: "notClicked", label: "Tıklanmadı", clicked: "false", paid: "", expired: "", countKey: "notClicked" },
    { key: "paid", label: "Ödendi", clicked: "", paid: "true", expired: "", countKey: "paid" },
    { key: "unpaid", label: "Ödenmedi", clicked: "", paid: "false", expired: "", countKey: "unpaid" },
    { key: "expired", label: "Süresi Dolmuş", clicked: "", paid: "", expired: "true", countKey: "expired" },
];

const EMPTY_STATS = {
    total: 0, active: 0, clicked: 0, notClicked: 0,
    paid: 0, unpaid: 0, expired: 0, collected: null, daily: [],
    // Seçili pencere vs önceki eşit pencere değişimi (%). null → karşılaştırma bazı yok.
    totalChange: null, activeChange: null, clickedChange: null, collectedChange: null,
};


// Özet kartlarının sağ üstündeki zaman aralığı filtresi.
// Seçilen aralık hem kart rakamlarını hem mini grafik serisini süzer (backend `days`).
const STATS_PERIODS = [
    { days: 7, label: "Son 7 Gün" },
    { days: 30, label: "Son 30 Gün" },
    { days: 90, label: "Son 90 Gün" },
    { days: 365, label: "Son 1 Yıl" },
];
const DEFAULT_STATS_DAYS = 30;

/** Grafik tooltip'i için sabit referans; her render'da yeniden üretilmemeli. */
const formatTryTooltip = (value) => formatAmountWithCurrency(value, "TRY");

/** Kart ikonu stili de sabit tutulur, aksi halde kart memo'su boşa çıkar. */
const CLICK_ICON_STYLE = { color: "#6f42c1" };

/** "2026-05-01" → "2026-05-01T00:00:00" (gün sonu için 23:59:59). */
const toIsoDateTime = (value, endOfDay = false) =>
    value ? `${value}T${endOfDay ? "23:59:59" : "00:00:00"}` : "";

/**
 * Sunucudan gelen yüzde değişimden (seçili pencere vs bir önceki eşit pencere) kart
 * eğilim rozetini üretir. changePct null ise: değer > 0 iken "yeni", aksi halde rozet yok.
 */
const trendFromPct = (changePct, rawValue = 0) => {
    if (changePct == null) return Number(rawValue) > 0 ? { up: true, label: "yeni" } : null;
    return { up: changePct >= 0, label: `%${Math.abs(changePct).toFixed(1).replace(".", ",")}` };
};

/**
 * Özet kartı: başlık, değer, eğilim ve son 30 günün grafiği.
 *
 * Grafiğin her render'da yeniden mount olmaması için bileşen modül seviyesinde tutulur.
 */
const StatCard = React.memo(({
                                 iconClass,
                                 iconStyle,
                                 iconVariant,
                                 label,
                                 value,
                                 series = [],
                                 labels = [],
                                 color,
                                 formatter,
                                 loading = false,
                                 trend = null,
                                 periodLabel = "son 30 gün",
                             }) => {
    // Eğilim (artış/azalış) sunucudan seçili pencereye göre gelir; grafik pencerenin tamamını çizer.
    const chartSeries = series;
    const chartLabels = labels;

    return (
        <div className="link-stat-card">
            <div className="link-stat-head">
                <div className={`mini-stat-icon ${iconVariant}`}>
                    <i className={`bx ${iconClass}`} style={iconStyle} />
                </div>
                <div className="text-muted small">{label}</div>
            </div>

            <div className="mini-stat-amount">
                {loading ? <Spinner size="sm" color="secondary" /> : value}
            </div>

            <div className="link-stat-trend">
                {trend ? (
                    <>
                        <span className={trend.up ? "up" : "down"}>
                            <i className={`bx ${trend.up ? "bx-trending-up" : "bx-trending-down"}`} /> {trend.label}
                        </span>
                        <span>{periodLabel}</span>
                    </>
                ) : (
                    <span>{periodLabel}</span>
                )}
            </div>

            <div className="link-stat-spark">
                <MiniSparkline
                    data={chartSeries}
                    categories={chartLabels}
                    color={color}
                    name={label}
                    formatter={formatter}
                />
            </div>
        </div>
    );
});

StatCard.displayName = "StatCard";

const PaymentLinksTable = () => {
    // Filtreler, sayfa ve açık panel URL'de taşınır: tam detay sayfasından geri
    // dönüldüğünde ekran aynı durumla açılsın diye.
    const [searchParams, setSearchParams] = useSearchParams();

    const [paymentLinks, setPaymentLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(() => parseInt(searchParams.get("page"), 10) || 0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [pageSize, setPageSize] = useState(() => parseInt(searchParams.get("size"), 10) || 10);
    const [searchTerm, setSearchTerm] = useState(() => searchParams.get("search") || "");
    const [shareOpenId, setShareOpenId] = useState(null);
    // Kopyalandı geri bildirimi: hangi satırın kopyala tuşunun üstünde balon görünecek.
    const [copiedId, setCopiedId] = useState(null);
    const copiedTimerRef = useRef(null);

    // Filtreler
    const [filterClicked, setFilterClicked] = useState(() => searchParams.get("clicked") || "");
    const [filterPaid, setFilterPaid] = useState(() => searchParams.get("paid") || "");
    const [filterExpired, setFilterExpired] = useState(() => searchParams.get("expired") || "");
    const [filterCompanyId, setFilterCompanyId] = useState(() => searchParams.get("companyId") || "");
    const [filterAccountId, setFilterAccountId] = useState(() => searchParams.get("accountId") || "");
    const [startDate, setStartDate] = useState(() => searchParams.get("startDate") || "");
    const [endDate, setEndDate] = useState(() => searchParams.get("endDate") || "");
    const [sortDir, setSortDir] = useState(() => searchParams.get("sortDir") || "desc");

    // Özet kartları / hızlı filtre sayaçları
    const [stats, setStats] = useState(EMPTY_STATS);
    const [statsLoading, setStatsLoading] = useState(true);
    // Kartların sağ üstündeki zaman aralığı filtresi (gün). Ref ile fetchStats her
    // çağrıldığında güncel değeri okur; ayrıca değişince kartlar yeniden çekilir.
    const [statsDays, setStatsDays] = useState(DEFAULT_STATS_DAYS);
    const statsDaysRef = useRef(statsDays);
    useEffect(() => { statsDaysRef.current = statsDays; }, [statsDays]);

    // Sağ detay paneli (null ise kapalı)
    const [detailLink, setDetailLink] = useState(null);
    // URL senkronu nesne kimliğine değil id'ye baksın (liste tazelendiğinde nesne değişiyor)
    const detailLinkId = detailLink?.id || "";
    // URL'yi senkronlayan efekt, geri yükleme efektinden önce çalışıp `detail`
    // parametresini siliyor; ilk değeri render sırasında saklıyoruz.
    const pendingDetailIdRef = useRef(searchParams.get("detail") || "");
    const [sharePanelOpen, setSharePanelOpen] = useState(false);
    const [statusLoadingId, setStatusLoadingId] = useState(null);

    const [qrLink, setQrLink] = useState(null);
    const [rotateTarget, setRotateTarget] = useState(null);
    const [rotateLoading, setRotateLoading] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [instantModalOpen, setInstantModalOpen] = useState(false);
    const [companyId, setCompanyId] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
    const linkCreationSuccess = useRef(false);

    const isFirstSearch = useRef(true);
    const searchRef = useRef(null);
    const animatedPlaceholder = useAnimatedPlaceholder(SEARCH_FIELDS.paymentLinks, { prefix: "Ara: " });
    const navigate = useNavigate();

    const pw = getPaymentWords(currentUser?.roles);
    document.title = `${pw.Word} Linkleri | Tahsilat360`;

    const isSuperAdmin = () => currentUser?.roles?.includes("ROLE_SUPER_ADMIN");

    const activeQuickKey = (QUICK_FILTERS.find(
        (f) => f.clicked === filterClicked && f.paid === filterPaid && f.expired === filterExpired
    ) || {}).key;

    const activeFilterCount =
        (searchTerm ? 1 : 0) +
        (filterClicked !== "" ? 1 : 0) +
        (filterPaid !== "" ? 1 : 0) +
        (filterExpired !== "" ? 1 : 0) +
        (filterCompanyId ? 1 : 0) +
        (startDate ? 1 : 0) +
        (endDate ? 1 : 0);

    /* ---------------- Veri ---------------- */

    /** Listeyi getirir. Verilmeyen alanlar için o anki filtre değerleri kullanılır. */
    const fetchPaymentLinks = async ({
                                         page = 0,
                                         size = pageSize,
                                         search = searchTerm,
                                         clicked = filterClicked,
                                         paid = filterPaid,
                                         expired = filterExpired,
                                         company = filterCompanyId,
                                         accountId = filterAccountId,
                                         start = startDate,
                                         end = endDate,
                                         sort = sortDir,
                                         first = false,
                                     } = {}) => {
        try {
            setError(null);
            if (first) setLoading(true); else setRefreshing(true);

            const params = { page, size };
            if (search && search.trim()) params.search = search.trim();
            if (clicked !== "" && clicked !== null && clicked !== undefined) params.clicked = clicked === "true";
            if (paid !== "" && paid !== null && paid !== undefined) params.paid = paid === "true";
            if (expired !== "" && expired !== null && expired !== undefined) params.expired = expired === "true";
            if (company && company.trim()) params.companyId = company.trim();
            if (accountId && accountId.trim()) params.accountId = accountId.trim();
            if (start) params.startDate = toIsoDateTime(start);
            if (end) params.endDate = toIsoDateTime(end, true);
            if (sort) params.sortDir = sort;

            const { data } = await axios.get(API.payment_links_list, { params, headers: {} });
            if (data.statusCode === 200) {
                const { content, totalElements: te, totalPages: tp, page: cp, size: ps } = data.data;
                setPaymentLinks(content);
                setTotalElements(te);
                setTotalPages(tp);
                setCurrentPage(cp);
                setPageSize(ps);
                return content;
            }
            setError(`${pw.Word} linkleri yüklenemedi`);
        } catch (err) {
            if (err.response?.status === 401 || err.response?.status === 403) {
                navigate("/login");
            } else {
                setError(err.response?.data?.message || `${pw.Word} linkleri yüklenirken bir hata oluştu`);
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
        return null;
    };

    /**
     * Özet kartları, hızlı filtre sayaçları ve grafik serisi.
     *
     * Tek istekle gelir: sayılar listeleme ile aynı yetki kapsamında hesaplanır,
     * tahsil edilen tutar ödeme kayıtlarından toplanır (arama terimine göre
     * daraltılmaz; yalnızca şirket/cari kapsamına uyar).
     */
    const fetchStats = useCallback(async ({ search = "", company = "", accountId = "", days } = {}) => {
        setStatsLoading(true);
        try {
            const params = { days: days ?? statsDaysRef.current };
            if (search && search.trim()) params.search = search.trim();
            if (company && company.trim()) params.companyId = company.trim();
            if (accountId && accountId.trim()) params.accountId = accountId.trim();

            const { data } = await axios.get(API.payment_links_stats, { params, headers: {} });
            if (data.statusCode === 200) {
                const s = data.data || {};
                setStats({
                    total: s.totalLinks || 0,
                    active: s.activeLinks || 0,
                    clicked: s.clickedLinks || 0,
                    notClicked: s.notClickedLinks || 0,
                    paid: s.paidLinks || 0,
                    unpaid: s.unpaidLinks || 0,
                    expired: s.expiredLinks || 0,
                    collected: s.totalCollected != null ? s.totalCollected : null,
                    daily: Array.isArray(s.daily) ? s.daily : [],
                    totalChange: s.totalLinksChange ?? null,
                    activeChange: s.activeLinksChange ?? null,
                    clickedChange: s.clickedLinksChange ?? null,
                    collectedChange: s.collectedChange ?? null,
                });
            } else {
                setStats(EMPTY_STATS);
            }
        } catch (e) {
            setStats(EMPTY_STATS);
        } finally {
            setStatsLoading(false);
        }
    }, []);

    // Zaman aralığı filtresi değişince yalnızca özet kartları/grafik yeniden çekilir.
    const onStatsPeriodChange = (days) => {
        if (days === statsDays) return;
        setStatsDays(days);
        fetchStats({ search: searchTerm, company: filterCompanyId, accountId: filterAccountId, days });
    };

    const refreshCurrentPage = () => fetchPaymentLinks({ page: currentPage });

    /** "Yenile": listeyi de özet kartlarını/grafikleri de tazeler. */
    const refreshAll = () => {
        refreshCurrentPage();
        fetchStats({ search: searchTerm, company: filterCompanyId, accountId: filterAccountId });
    };

    /** Filtre değişimlerinde listeyi ilk sayfadan getirir; kapsam değiştiyse özeti de tazeler. */
    const applyFilters = (next = {}) => {
        const clicked = next.clicked !== undefined ? next.clicked : filterClicked;
        const paid = next.paid !== undefined ? next.paid : filterPaid;
        const expired = next.expired !== undefined ? next.expired : filterExpired;
        const company = next.companyId !== undefined ? next.companyId : filterCompanyId;
        const search = next.searchTerm !== undefined ? next.searchTerm : searchTerm;
        const start = next.startDate !== undefined ? next.startDate : startDate;
        const end = next.endDate !== undefined ? next.endDate : endDate;

        setCurrentPage(0);
        fetchPaymentLinks({ page: 0, search, clicked, paid, expired, company, start, end });

        // Sayaçlar tıklanma/ödeme/süre kırılımını gösterdiği için yalnızca kapsam
        // filtreleri (arama, şirket) değişince yenilenir.
        if (next.searchTerm !== undefined || next.companyId !== undefined) {
            fetchStats({ search, company, accountId: filterAccountId });
        }
    };

    const handleQuickFilter = (f) => {
        setFilterClicked(f.clicked);
        setFilterPaid(f.paid);
        setFilterExpired(f.expired);
        applyFilters({ clicked: f.clicked, paid: f.paid, expired: f.expired });
    };

    /** Oluşturulma kolonunda sıralama yönünü değiştirir. */
    const toggleSort = () => {
        const next = sortDir === "desc" ? "asc" : "desc";
        setSortDir(next);
        setCurrentPage(0);
        fetchPaymentLinks({ page: 0, sort: next });
    };

    const handleClearFilters = () => {
        setSearchTerm("");
        setFilterClicked("");
        setFilterPaid("");
        setFilterExpired("");
        setFilterCompanyId("");
        setStartDate("");
        setEndDate("");
        setCurrentPage(0);
        fetchPaymentLinks({ page: 0, search: "", clicked: "", paid: "", expired: "", company: "", start: "", end: "" });
        fetchStats({ accountId: filterAccountId });
    };

    /* ---------------- Aksiyonlar ---------------- */

    const gotoCreateInstantLinkPage = () => {
        if (!companyId) {
            toast.warn("Firma bilgisi alınamadı. Lütfen tekrar deneyin.");
            return;
        }
        navigate(`/payment-links/create/${companyId}`);
    };

    const handleInstantModalToggle = () => {
        if (instantModalOpen && !linkCreationSuccess.current) {
            setShowCloseConfirmation(true);
        } else {
            setInstantModalOpen(!instantModalOpen);
            if (linkCreationSuccess.current) linkCreationSuccess.current = false;
        }
    };

    const handleLinkCreationSuccess = () => { linkCreationSuccess.current = true; };
    const confirmCloseModal = () => { setInstantModalOpen(false); setShowCloseConfirmation(false); };
    const cancelCloseModal = () => { setShowCloseConfirmation(false); };

    const handleModalSave = () => {
        refreshCurrentPage();
        fetchStats({ search: searchTerm, company: filterCompanyId, accountId: filterAccountId });
    };

    const toggleDetail = (link) => {
        setSharePanelOpen(false);
        setDetailLink((prev) => (prev && prev.id === link.id ? null : link));
    };

    const closeDetail = () => {
        setSharePanelOpen(false);
        setDetailLink(null);
    };

    /**
     * Paylaşım yalnızca seçilen kanal gerçekten gönderildiğinde başarı sayılır.
     * Popover'ı burada kapatmıyoruz; kendi başarı mesajını gösterip kapanıyor.
     */
    const handleShareSuccess = (res) => {
        toast.success(
            res?.channel === "WHATSAPP"
                ? "WhatsApp paylaşımı açıldı"
                : res?.channel === "EMAIL"
                    ? `${pw.Word} linki e-posta ile gönderildi`
                    : `${pw.Word} linki SMS ile gönderildi`
        );
    };

    /**
     * @param {string} url  kopyalanacak adres
     * @param {string} [linkId]  verilirse o satırın kopyala tuşunun üstünde
     *   kısa süreli "Kopyalandı" balonu gösterilir (toast yerine, buton başında).
     */
    const handleCopyUrl = async (url, linkId) => {
        await copyToClipboard(url);
        if (!linkId) {
            toast.success("Link adresi kopyalandı");
            return;
        }
        clearTimeout(copiedTimerRef.current);
        setCopiedId(linkId);
        copiedTimerRef.current = setTimeout(() => setCopiedId(null), 1500);
    };

    // Balon açıkken satırlar değişirse (sayfa/filtre/yenileme) hedef buton DOM'dan
    // kalkar ve Tooltip boşta kalır; listeyle birlikte kapatılır.
    useEffect(() => {
        clearTimeout(copiedTimerRef.current);
        setCopiedId(null);
    }, [paymentLinks]);

    useEffect(() => () => clearTimeout(copiedTimerRef.current), []);

    const handleOpenLink = (link) => {
        if (link.paymentLinkUrl) window.open(link.paymentLinkUrl, "_blank");
    };

    const handleToggleStatus = async (link) => {
        const newStatus = link.status === "ACTIVE" ? "PAUSED" : "ACTIVE";
        setStatusLoadingId(link.id);
        try {
            const res = await updatePaymentLinkStatus(link.id, newStatus);
            const json = await res.json();
            if (res.ok) {
                toast.success(newStatus === "PAUSED"
                    ? "Bağlantı duraklatıldı. Adres değişmedi; yeniden açtığınızda aynı adres çalışır."
                    : `Bağlantı yeniden açıldı. ${pw.Word} kabul edilmeye başladı.`);
                await refreshCurrentPage();
            } else {
                toast.error(json.message || "Link durumu güncellenemedi");
            }
        } catch {
            toast.error("Link durumu güncellenemedi");
        } finally {
            setStatusLoadingId(null);
        }
    };

    const handleRotateToken = async () => {
        if (!rotateTarget) return;
        setRotateLoading(true);
        try {
            const res = await rotatePaymentLinkToken(rotateTarget.id);
            const json = await res.json();
            if (res.ok) {
                toast.success("Link adresi yenilendi; yeni adresi paylaşmayı unutmayın");
                await refreshCurrentPage();
                setRotateTarget(null);
            } else {
                toast.error(json.message || "Link adresi yenilenemedi");
            }
        } catch {
            toast.error("Link adresi yenilenemedi");
        } finally {
            setRotateLoading(false);
        }
    };

    /**
     * Silinen link firma kullanıcısının listesinden tamamen kaybolduğu için
     * açık detay paneli de kapatılır — aksi hâlde panel artık listede olmayan
     * bir kaydı göstermeye devam ederdi.
     */
    const handleDeleteLink = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        try {
            const res = await deletePaymentLink(deleteTarget.id);
            // Silme gövdesiz yanıt dönebilir; json() patlamasın.
            const json = await res.json().catch(() => ({}));
            if (res.ok) {
                toast.success(`${pw.Word} bağlantısı silindi`);
                if (detailLink?.id === deleteTarget.id) closeDetail();
                setDeleteTarget(null);
                await refreshCurrentPage();
            } else {
                toast.error(json.message || "Bağlantı silinemedi");
            }
        } catch {
            toast.error("Bağlantı silinemedi");
        } finally {
            setDeleteLoading(false);
        }
    };

    /* ---------------- Sayfalama ---------------- */

    const handlePageChange = (p) => {
        if (p >= 0 && p < totalPages) {
            setCurrentPage(p);
            fetchPaymentLinks({ page: p });
        }
    };

    const handlePageSizeChange = (sz) => {
        setPageSize(sz);
        setCurrentPage(0);
        fetchPaymentLinks({ page: 0, size: sz });
    };

    /* ---------------- Efektler ---------------- */

    useEffect(() => {
        // URL'den gelen sayfa/filtrelerle açılır
        fetchPaymentLinks({ page: currentPage, first: true });
        fetchStats({ search: searchTerm, company: filterCompanyId, accountId: filterAccountId });
    }, []);

    // Ekran durumunu URL'ye yazar (geçmişi kirletmemek için replace ile).
    // Varsayılan değerler adres çubuğunda görünmesin diye atlanır.
    useEffect(() => {
        const state = {
            page: currentPage || "",
            size: pageSize === 10 ? "" : pageSize,
            search: searchTerm,
            clicked: filterClicked,
            paid: filterPaid,
            expired: filterExpired,
            companyId: filterCompanyId,
            accountId: filterAccountId,
            startDate,
            endDate,
            sortDir: sortDir === "desc" ? "" : sortDir,
            detail: detailLinkId,
        };

        const params = new URLSearchParams();
        Object.entries(state).forEach(([key, value]) => {
            if (value !== "" && value !== null && value !== undefined) {
                params.set(key, String(value));
            }
        });
        setSearchParams(params, { replace: true });
    }, [
        currentPage, pageSize, searchTerm, filterClicked, filterPaid, filterExpired,
        filterCompanyId, filterAccountId, startDate, endDate, sortDir, detailLinkId,
        setSearchParams,
    ]);

    /**
     * Liste her tazelendiğinde detay panelini senkronlar:
     *  - URL'de `detail` varsa (tam detay sayfasından geri dönüldü) paneli yeniden açar
     *  - Açık panelin verisini güncel satırla eşitler
     *  - Link listede yoksa (filtre dışında kaldı) paneli kapatır
     */
    useEffect(() => {
        if (loading) return;

        const targetId = detailLinkId || pendingDetailIdRef.current;
        if (!targetId) return;

        pendingDetailIdRef.current = "";
        setDetailLink(paymentLinks.find((l) => l.id === targetId) || null);
    }, [paymentLinks, loading, detailLinkId]);

    // Sağ panelin sticky kalabilmesi için tema kısıtını yalnızca bu sayfada gevşetiyoruz
    useEffect(() => {
        const cls = `${PAYMENT_LINK_SCOPE}-page`;
        document.body.classList.add(cls);
        return () => document.body.classList.remove(cls);
    }, []);

    useEffect(() => {
        const loadUser = async () => {
            try {
                const res = await fetchAuthMe();
                if (res.ok) {
                    const { data } = await res.json();
                    setCurrentUser(data);
                    setCompanyId(data.companyId);
                }
            } catch (e) { /* no-op */ }
        };
        loadUser();
    }, []);

    useEffect(() => {
        if (!isSuperAdmin()) return;
        const loadCompanies = async () => {
            try {
                const res = await fetchCompaniesList();
                if (res.ok) {
                    const { data } = await res.json();
                    setCompanies(data || []);
                }
            } catch (e) { /* no-op */ }
        };
        loadCompanies();
    }, [currentUser]);

    // Arama: mevcut 500 ms debounce davranışı korunur
    useEffect(() => {
        if (isFirstSearch.current) { isFirstSearch.current = false; return; }
        const t = setTimeout(() => {
            setCurrentPage(0);
            fetchPaymentLinks({ page: 0 });
            fetchStats({ search: searchTerm, company: filterCompanyId, accountId: filterAccountId });
        }, 500);
        return () => clearTimeout(t);
    }, [searchTerm]);

    /* ---------------- Render ---------------- */

    const dailyLabels = useMemo(() => stats.daily.map((d) => formatDate(d.date)), [stats.daily]);

    // Diziler her render'da yeniden üretilirse dört grafik de gereksiz yere güncelleniyor
    const series = useMemo(() => {
        const of = (key) => stats.daily.map((d) => Number(d[key] || 0));
        return {
            created: of("created"),
            active: of("active"),
            clicked: of("clicked"),
            collected: of("collected"),
        };
    }, [stats.daily]);

    // Kart eğilim rozetinin altında yazan dönem metni (seçili aralığa göre).
    const trendPeriodLabel = (STATS_PERIODS.find((p) => p.days === statsDays)?.label || "Son 30 Gün").toLowerCase();

    const renderCreateButton = (extraClass = "") => (
        <>
            <Button
                className={`d-none d-md-inline-flex align-items-center gap-1 btn-rounded btn-navy ${extraClass}`}
                onClick={gotoCreateInstantLinkPage}
                disabled={!companyId}
            >
                <i className="bx bx-plus" /> {pw.Word} Linki Oluştur
            </Button>
            <Button
                className={`d-inline-flex d-md-none align-items-center gap-1 btn-rounded btn-navy ${extraClass}`}
                onClick={handleInstantModalToggle}
                disabled={!companyId}
            >
                <i className="bx bx-plus" /> {pw.Word} Linki Oluştur
            </Button>
        </>
    );

    return (
        <div className={`page-content ${PAYMENT_LINK_SCOPE}`}>
            <style>{PAYMENT_LINK_STYLES}</style>
            <div className="container-fluid">
                {/* Sayfa başlığı */}
                <div className="mb-3">
                    <h4 className="mb-1 fw-bold">{pw.Word} Linkleri</h4>
                    <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                        Oluşturduğunuz {pw.word} linklerini yönetin ve performanslarını takip edin.
                    </p>
                </div>

                {/* Dönem filtresi + özet kartlar satırın DIŞINDA: detay paneli açıldığında
                    sol kolon daralıyor; burada kalırlarsa onlar da daralıp bozuluyordu.
                    Böylece kartlar hep tam genişlik, panel de tablonun hizasından açılır. */}
                {/* Zaman aralığı filtresi — özet kartlarının sağ üstünde durur */}
                <div className="d-flex justify-content-end mb-2">
                    <UncontrolledDropdown>
                        <DropdownToggle
                            caret
                            size="sm"
                            color="light"
                            className="border d-inline-flex align-items-center gap-1"
                            style={{ borderRadius: 999, fontWeight: 600, background: "#fff" }}
                        >
                            <i className="bx bx-calendar" />
                            {(STATS_PERIODS.find((p) => p.days === statsDays) || {}).label || "Dönem"}
                        </DropdownToggle>
                        <DropdownMenu end>
                            {STATS_PERIODS.map((p) => (
                                <DropdownItem
                                    key={p.days}
                                    active={p.days === statsDays}
                                    onClick={() => onStatsPeriodChange(p.days)}
                                >
                                    {p.label}
                                </DropdownItem>
                            ))}
                        </DropdownMenu>
                    </UncontrolledDropdown>
                </div>

                {/* Özet kartları — seçilen zaman aralığındaki toplamlar ve günlük seyir */}
                <Row className="g-3 mb-3">
                    <Col sm={6} xl={3}>
                        <StatCard
                            iconClass="bx-link"
                            iconVariant="total"
                            label="Toplam Link"
                            value={stats.total}
                            series={series.created}
                            labels={dailyLabels}
                            color={COLORS.NAVY}
                            loading={statsLoading}
                            trend={trendFromPct(stats.totalChange, stats.total)}
                            periodLabel={trendPeriodLabel}
                        />
                    </Col>

                    <Col sm={6} xl={3}>
                        <StatCard
                            iconClass="bx-check-circle text-success"
                            iconVariant="success"
                            label="Aktif Link"
                            value={stats.active}
                            series={series.active}
                            labels={dailyLabels}
                            color="#2E7D32"
                            loading={statsLoading}
                            trend={trendFromPct(stats.activeChange, stats.active)}
                            periodLabel={trendPeriodLabel}
                        />
                    </Col>

                    <Col sm={6} xl={3}>
                        <StatCard
                            iconClass="bx-mouse"
                            iconStyle={CLICK_ICON_STYLE}
                            iconVariant="info"
                            label="Tıklanan Link"
                            value={stats.clicked}
                            series={series.clicked}
                            labels={dailyLabels}
                            color="#6f42c1"
                            loading={statsLoading}
                            trend={trendFromPct(stats.clickedChange, stats.clicked)}
                            periodLabel={trendPeriodLabel}
                        />
                    </Col>

                    <Col sm={6} xl={3}>
                        <StatCard
                            iconClass="bx-wallet text-warning"
                            iconVariant="pending"
                            label="Tahsil Edilen Tutar"
                            value={stats.collected != null ? formatAmountWithCurrency(stats.collected, "TRY") : "-"}
                            series={series.collected}
                            labels={dailyLabels}
                            color={COLORS.ORANGE}
                            formatter={formatTryTooltip}
                            loading={statsLoading}
                            trend={trendFromPct(stats.collectedChange, stats.collected)}
                            periodLabel={trendPeriodLabel}
                        />
                    </Col>
                </Row>

                <Row className="g-3">
                    {/* Liste — detay paneli açıkken daralır */}
                    <Col xl={detailLink ? 8 : 12} xxl={detailLink ? 9 : 12} className="ph-main">
                        <TableCard data-tour="payment-links-card">
                            <CardHeader className="bg-white border-0 rounded-top-4" data-tour="payment-links-header">
                                {/* Filtre alanı — seçim yapıldığı anda liste güncellenir */}
                                <div className="filter-bar">
                                    <div className="d-flex flex-wrap gap-2">
                                        <div className="filter-field">
                                            <span className="filter-label">Arama</span>
                                            <Input
                                                innerRef={searchRef}
                                                type="search"
                                                placeholder={animatedPlaceholder}
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="pill-input"
                                                inputMode="search"
                                                autoComplete="off"
                                                autoCorrect="off"
                                                spellCheck={false}
                                            />
                                        </div>

                                        <div className="filter-field">
                                            <span className="filter-label">Başlangıç</span>
                                            <DateInput
                                                value={startDate}
                                                maxDate={endDate || undefined}
                                                onChange={(iso) => { setStartDate(iso); applyFilters({ startDate: iso }); }}
                                                className="pill-input"
                                            />
                                        </div>

                                        <div className="filter-field">
                                            <span className="filter-label">Bitiş</span>
                                            <DateInput
                                                value={endDate}
                                                minDate={startDate || undefined}
                                                onChange={(iso) => { setEndDate(iso); applyFilters({ endDate: iso }); }}
                                                className="pill-input"
                                            />
                                        </div>

                                        <div className="filter-field">
                                            <span className="filter-label">Tıklanma Durumu</span>
                                            <Input
                                                type="select"
                                                value={filterClicked}
                                                onChange={(e) => { setFilterClicked(e.target.value); applyFilters({ clicked: e.target.value }); }}
                                                className="pill-select"
                                            >
                                                <option value="">Tümü</option>
                                                <option value="true">Tıklanmış</option>
                                                <option value="false">Tıklanmamış</option>
                                            </Input>
                                        </div>

                                        <div className="filter-field">
                                            <span className="filter-label">{pw.Word} Durumu</span>
                                            <Input
                                                type="select"
                                                value={filterPaid}
                                                onChange={(e) => { setFilterPaid(e.target.value); applyFilters({ paid: e.target.value }); }}
                                                className="pill-select"
                                            >
                                                <option value="">Tümü</option>
                                                <option value="true">Ödenmiş</option>
                                                <option value="false">Ödenmemiş</option>
                                            </Input>
                                        </div>

                                        {isSuperAdmin() && (
                                            <div className="filter-field">
                                                <span className="filter-label">Şirket / Firma</span>
                                                <Input
                                                    type="select"
                                                    value={filterCompanyId}
                                                    onChange={(e) => { setFilterCompanyId(e.target.value); applyFilters({ companyId: e.target.value }); }}
                                                    className="pill-select"
                                                >
                                                    <option value="">Tümü</option>
                                                    {companies.map((c) => (
                                                        <option key={c.id} value={c.id}>{c.companyName}</option>
                                                    ))}
                                                </Input>
                                            </div>
                                        )}
                                    </div>

                                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3 filter-actions">
                                        <div className="d-flex align-items-center gap-2">
                                            {activeFilterCount > 0 && (
                                                <span className="chip chip-navy">{activeFilterCount} filtre aktif</span>
                                            )}
                                            <Button
                                                color="light"
                                                className="btn-rounded"
                                                onClick={handleClearFilters}
                                                disabled={activeFilterCount === 0}
                                            >
                                                <i className="bx bx-x me-1" /> Temizle
                                            </Button>
                                        </div>

                                        {renderCreateButton()}
                                    </div>
                                </div>

                                {/* Hızlı filtreler */}
                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                                    <div className="d-flex align-items-center gap-2 quick-filter-bar">
                                        {QUICK_FILTERS.map((f) => (
                                            <button
                                                key={f.key}
                                                type="button"
                                                className={`quick-filter ${activeQuickKey === f.key ? "active" : ""}`}
                                                onClick={() => handleQuickFilter(f)}
                                            >
                                                {f.label}
                                                <span className="quick-filter-count">
                                                    {statsLoading ? "…" : stats[f.countKey]}
                                                </span>
                                            </button>
                                        ))}
                                    </div>

                                    <button
                                        className="btn btn-light btn-sm btn-rounded"
                                        onClick={refreshAll}
                                        title="Listeyi ve özet kartlarını yenile"
                                    >
                                        <i className={`bx bx-refresh ${refreshing ? "bx-spin" : ""}`} />
                                    </button>
                                </div>
                            </CardHeader>

                            <CardBody className="pt-2">
                                {error && (
                                    <Alert color="danger" className="mb-3 rounded-4">
                                        <i className="bx bx-error-circle me-2" />
                                        {error}
                                        <button className="btn btn-sm btn-outline-danger ms-2 btn-rounded" onClick={refreshCurrentPage}>
                                            Tekrar Dene
                                        </button>
                                    </Alert>
                                )}

                                {loading ? (
                                    <div className="text-center p-5">
                                        <Spinner size="lg" color="primary" />
                                        <h6 className="mt-3 text-muted">{pw.Word} linkleri yükleniyor...</h6>
                                    </div>
                                ) : (
                                    <>
                                        <div className={`table-responsive rounded-4 border ${refreshing ? "opacity-50" : ""}`}>
                                            <Table className="table table-hover mb-0">
                                                <Thead>
                                                    <Tr>
                                                        <Th className="text-center">
                                                            <button
                                                                type="button"
                                                                className="sort-th"
                                                                onClick={toggleSort}
                                                                title={sortDir === "desc" ? "En yeni önce — tersine çevir" : "En eski önce — tersine çevir"}
                                                            >
                                                                Oluşturulma
                                                                <i className={`bx ${sortDir === "desc" ? "bx-sort-down" : "bx-sort-up"}`} />
                                                            </button>
                                                        </Th>
                                                        <Th className="d-none d-md-table-cell text-center">Şirket / Cari</Th>
                                                        <Th className="d-none d-xl-table-cell text-center">Oluşturan</Th>
                                                        <Th className="text-center">Tutar</Th>
                                                        <Th className="d-none d-lg-table-cell text-center">Analitik</Th>
                                                        <Th className="text-center">Durum</Th>
                                                        <Th className="text-center">İşlemler</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {paymentLinks.length ? paymentLinks.map((link) => {
                                                        const status = getLinkStatus(link);
                                                        const clickedChip = getClickedChip(link);
                                                        const paymentChip = getPaymentChip(link);

                                                        return (
                                                            <Tr
                                                                key={link.id}
                                                                className={`border-bottom row-clickable${link.deleted ? " row-deleted" : ""}${detailLink?.id === link.id ? " row-selected" : ""}`}
                                                                onClick={() => toggleDetail(link)}
                                                            >
                                                                <Td className="text-center">
                                                                    <div className="fw-semibold text-dark">{formatDate(link.createdAt)}</div>
                                                                    <div className="cell-sub">{formatTime(link.createdAt)}</div>
                                                                </Td>

                                                                <Td className="d-none d-md-table-cell text-center">
                                                                    <TruncatedText
                                                                        text={link.companyName}
                                                                        maxLength={24}
                                                                        className="fw-semibold text-dark"
                                                                    />
                                                                    <div className="cell-sub">
                                                                        <TruncatedText text={link.accountName || "-"} maxLength={26} />
                                                                    </div>
                                                                </Td>

                                                                <Td className="d-none d-xl-table-cell text-center">
                                                                    <TruncatedText text={link.creatorName} maxLength={18} />
                                                                </Td>

                                                                <Td className="text-center">
                                                                    <strong>
                                                                        {link.fixedAmount != null
                                                                            ? formatAmountWithCurrency(link.fixedAmount, link.currency)
                                                                            : (link.totalCollected != null && Number(link.totalCollected) > 0
                                                                                ? formatAmountWithCurrency(link.totalCollected, link.currency)
                                                                                : "-")}
                                                                    </strong>
                                                                    {link.linkType === "FIXED" ? (
                                                                        <div className="cell-sub">Sabit link</div>
                                                                    ) : (link.fixedAmount == null && Number(link.totalCollected) > 0 && (
                                                                        <div className="cell-sub">Tahsil edilen</div>
                                                                    ))}
                                                                </Td>

                                                                {/* "Ödeme Linki" sütunu kaldırıldı: adresin tabloda
                                                                    görünmesine gerek yok; kopyala/paylaş İşlemler
                                                                    sütununda, tam adres sağ detay panelinde. */}

                                                                {/* Referanstaki gibi ikon + sayı: kaç kez açıldı, kaç ödeme alındı */}
                                                                <Td className="d-none d-lg-table-cell text-center">
                                                                    <div className="d-flex flex-wrap gap-1 justify-content-center">
                                                                        <span className={`chip ${clickedChip.chipClass}`} title={clickedChip.label}>
                                                                            <i className="bx bx-show" /> {link.clickCount || 0}
                                                                        </span>
                                                                        <span className={`chip ${paymentChip.chipClass}`} title={paymentChip.label}>
                                                                            <i className="bx bx-credit-card" />{" "}
                                                                            {link.linkType === "FIXED"
                                                                                ? link.paymentCount || 0
                                                                                : (link.userPaid ? 1 : 0)}
                                                                        </span>
                                                                    </div>
                                                                </Td>

                                                                <Td className="text-center">
                                                                    <span className={`chip ${status.chipClass}`}>{status.label}</span>
                                                                </Td>

                                                                <Td className="text-center" onClick={(e) => e.stopPropagation()}>
                                                                    <div className="d-flex gap-1 justify-content-center align-items-center">
                                                                        <button
                                                                            className="btn btn-sm action-btn btn-orange"
                                                                            title={detailLink?.id === link.id ? "Detayı kapat" : "Detay"}
                                                                            onClick={() => toggleDetail(link)}
                                                                        >
                                                                            <i className="bx bx-show" />
                                                                        </button>

                                                                        <button
                                                                            id={`copy-btn-${link.id}`}
                                                                            className="btn btn-sm action-btn btn-light"
                                                                            title="Link adresini kopyala"
                                                                            onClick={() => handleCopyUrl(link.paymentLinkUrl, link.id)}
                                                                            disabled={!link.paymentLinkUrl}
                                                                        >
                                                                            <i className={`bx ${copiedId === link.id ? "bx-check text-success" : "bx-copy"}`} />
                                                                        </button>
                                                                        {/* Kopyalama geri bildirimi tuşun üstünde; toast yerine
                                                                            burada, çünkü kullanıcı zaten butona bakıyor. */}
                                                                        <Tooltip
                                                                            isOpen={copiedId === link.id}
                                                                            target={`copy-btn-${link.id}`}
                                                                            placement="top"
                                                                            // Kendi kendine açılıp kapanmasın: görünürlüğü copiedId belirler.
                                                                            trigger="manual"
                                                                            toggle={() => {}}
                                                                        >
                                                                            Kopyalandı
                                                                        </Tooltip>

                                                                        <button
                                                                            id={`share-btn-${link.id}`}
                                                                            className="btn btn-sm action-btn btn-navy"
                                                                            title="Paylaş"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setShareOpenId((prev) => (prev === link.id ? null : link.id));
                                                                            }}
                                                                        >
                                                                            <i className="bx bx-share-alt" />
                                                                        </button>

                                                                        <UncontrolledDropdown>
                                                                            <DropdownToggle
                                                                                className="btn btn-sm action-btn btn-light"
                                                                                title="Diğer işlemler"
                                                                            >
                                                                                <i className="bx bx-dots-vertical-rounded" />
                                                                            </DropdownToggle>
                                                                            <DropdownMenu end>
                                                                                <DropdownItem
                                                                                    onClick={() => handleOpenLink(link)}
                                                                                    disabled={!link.paymentLinkUrl}
                                                                                >
                                                                                    <i className="bx bx-link-external me-1" /> Bağlantıyı Aç
                                                                                </DropdownItem>
                                                                                <DropdownItem
                                                                                    onClick={() => setQrLink(link)}
                                                                                    disabled={!link.paymentLinkUrl}
                                                                                >
                                                                                    <i className="bx bx-qr me-1" /> QR Oluştur
                                                                                </DropdownItem>
                                                                                {/* Durum değişimi ve adres yenileme backend'de yalnızca sabit linkler için izinli */}
                                                                                {canToggleLinkStatus(link) && (
                                                                                    <>
                                                                                        <DropdownItem divider />
                                                                                        <DropdownItem
                                                                                            onClick={() => handleToggleStatus(link)}
                                                                                            disabled={statusLoadingId === link.id}
                                                                                        >
                                                                                            <i className={`bx ${link.status === "ACTIVE" ? "bx-pause-circle" : "bx-play-circle"} me-1`} />
                                                                                            {link.status === "ACTIVE" ? " Duraklat" : " Yeniden Aç"}
                                                                                        </DropdownItem>
                                                                                        <DropdownItem onClick={() => setRotateTarget(link)}>
                                                                                            <i className="bx bx-revision me-1" /> Link Adresini Yenile
                                                                                        </DropdownItem>
                                                                                    </>
                                                                                )}
                                                                                {/* Silme yalnız SUPER_ADMIN / COMPANY_ADMIN'de (backend kısıtı) */}
                                                                                {canDeleteLink(link, currentUser?.roles) && (
                                                                                    <>
                                                                                        <DropdownItem divider />
                                                                                        <DropdownItem
                                                                                            className="text-danger"
                                                                                            onClick={() => setDeleteTarget(link)}
                                                                                        >
                                                                                            <i className="bx bx-trash me-1" /> Linki Sil
                                                                                        </DropdownItem>
                                                                                    </>
                                                                                )}
                                                                                <DropdownItem divider />
                                                                                <DropdownItem onClick={() => navigate(`/payment-links/${link.id}`)}>
                                                                                    <i className="bx bx-detail me-1" /> Tam Detay Sayfası
                                                                                </DropdownItem>
                                                                            </DropdownMenu>
                                                                        </UncontrolledDropdown>

                                                                        <SharePaymentLinkPopover
                                                                            isOpen={shareOpenId === link.id}
                                                                            toggle={() => setShareOpenId(null)}
                                                                            targetId={`share-btn-${link.id}`}
                                                                            link={link}
                                                                            onSuccess={handleShareSuccess}
                                                                        />
                                                                    </div>
                                                                </Td>
                                                            </Tr>
                                                        );
                                                    }) : (
                                                        <Tr>
                                                            <Td colSpan="7" className="text-center py-4">
                                                                <div className="text-muted">
                                                                    <i className="bx bx-link display-4 d-block mb-2" />
                                                                    {pw.Word} linki bulunamadı
                                                                    {(searchTerm || activeFilterCount > 0) && (
                                                                        <div className="mt-2"><small>Arama teriminizi veya filtreleri değiştirmeyi deneyin</small></div>
                                                                    )}
                                                                </div>
                                                            </Td>
                                                        </Tr>
                                                    )}
                                                </Tbody>
                                            </Table>
                                        </div>

                                        {totalElements > 0 && (
                                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                                                <div className="text-muted" style={{ fontSize: ".9rem" }}>
                                                    {totalElements} kayıttan {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalElements)} arası gösteriliyor
                                                </div>
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="page-size-wrap">
                                                        <span className="text-muted">Sayfa başına</span>
                                                        <select
                                                            className="form-select form-select-sm rounded-pill page-size-select"
                                                            value={pageSize}
                                                            onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                                                        >
                                                            {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                                                        </select>
                                                    </div>
                                                    <nav>
                                                        <ul className="pagination pagination-sm mb-0 corporate-pagination">
                                                            <li className={`page-item ${currentPage === 0 ? "disabled" : ""}`}>
                                                                <button className="page-link" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0}>
                                                                    <i className="bx bx-chevron-left" />
                                                                </button>
                                                            </li>
                                                            {getPaginationNumbers(currentPage, totalPages).map((p) => (
                                                                <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
                                                                    <button className="page-link" onClick={() => handlePageChange(p)}>{p + 1}</button>
                                                                </li>
                                                            ))}
                                                            <li className={`page-item ${currentPage >= totalPages - 1 ? "disabled" : ""}`}>
                                                                <button className="page-link" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1}>
                                                                    <i className="bx bx-chevron-right" />
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    </nav>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </CardBody>
                        </TableCard>
                    </Col>

                    {/* Seçilen linkin detay paneli — xl'de sağda sabit, altında sağdan açılan overlay */}
                    {detailLink && (
                        <>
                            <div className="pd-backdrop d-xl-none" onClick={closeDetail} />
                            <Col xl={4} xxl={3} className="ph-side">
                                <PaymentLinkDetailPanel
                                    link={detailLink}
                                    onClose={closeDetail}
                                    shareButtonId={PANEL_SHARE_BTN_ID}
                                    statusLoading={statusLoadingId === detailLink.id}
                                    actions={{
                                        onOpenLink: handleOpenLink,
                                        onShowQr: (l) => setQrLink(l),
                                        onShare: () => setSharePanelOpen((v) => !v),
                                        onToggleStatus: handleToggleStatus,
                                        onRotateToken: (l) => setRotateTarget(l),
                                        onDeleteLink: (l) => setDeleteTarget(l),
                                        onOpenFullDetail: (l) => navigate(`/payment-links/${l.id}`),
                                    }}
                                    roles={currentUser?.roles}
                                >
                                    <SharePaymentLinkPopover
                                        isOpen={sharePanelOpen}
                                        toggle={() => setSharePanelOpen(false)}
                                        targetId={PANEL_SHARE_BTN_ID}
                                        link={detailLink}
                                        onSuccess={handleShareSuccess}
                                    />
                                </PaymentLinkDetailPanel>
                            </Col>
                        </>
                    )}
                </Row>

                {/* Modallar */}
                <PaymentLinkQrModal
                    isOpen={!!qrLink}
                    toggle={() => setQrLink(null)}
                    link={qrLink}
                />

                <PaymentLinkRotateTokenModal
                    isOpen={!!rotateTarget}
                    toggle={() => !rotateLoading && setRotateTarget(null)}
                    link={rotateTarget}
                    onConfirm={handleRotateToken}
                    loading={rotateLoading}
                />

                <PaymentLinkDeleteModal
                    isOpen={!!deleteTarget}
                    toggle={() => !deleteLoading && setDeleteTarget(null)}
                    link={deleteTarget}
                    onConfirm={handleDeleteLink}
                    loading={deleteLoading}
                />

                <InstantPaymentModal
                    isOpen={instantModalOpen}
                    toggle={handleInstantModalToggle}
                    companyId={companyId}
                    onSaved={handleModalSave}
                    onSuccess={handleLinkCreationSuccess}
                />

                {/* Kapatma Onayı */}
                <Modal isOpen={showCloseConfirmation} toggle={cancelCloseModal} centered>
                    <ModalHeader toggle={cancelCloseModal}>Modal Kapatma Onayı</ModalHeader>
                    <ModalBody>
                        <div className="text-center">
                            <i className="bx bx-question-mark display-4 text-warning mb-3" />
                            <h5>Emin misiniz?</h5>
                            <p className="text-muted">
                                {pw.Word} linki oluşturma işlemini iptal etmek istediğinizden emin misiniz? Girdiğiniz veriler kaybolacaktır.
                            </p>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={cancelCloseModal}>Hayır, Devam Et</Button>
                        <Button color="danger" onClick={confirmCloseModal}>Evet, Kapat</Button>
                    </ModalFooter>
                </Modal>
            </div>
        </div>
    );
};

export default PaymentLinksTable;
